import {
  formatMDYDotsUTC,
  graphViewToUrlPeriod,
  urlPeriodToGraphView,
  type DashboardGraphView,
} from '@/utils/dashboard/dateHelpers'
import {
  clampRangeToDayBounds,
  normalizeRangeChronology,
} from '@/utils/dashboard/timeframeRangeHelpers'

export const DASHBOARD_URL_DATE_PATTERN = /^\d{2}\.\d{2}\.\d{4}$/
export const DASHBOARD_MIN_RANGE_DAYS = 3
export const DASHBOARD_MAX_RANGE_DAYS = 731

export const parseDashboardUrlDate = (value: string | null) => {
  if (!value || !DASHBOARD_URL_DATE_PATTERN.test(value)) return null
  const [monthPart, dayPart, yearPart] = value.split('.')
  const month = Number(monthPart)
  const day = Number(dayPart)
  const year = Number(yearPart)
  if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(year)) return null
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1000) return null

  const parsed = new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0))
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null
  }

  return parsed
}

export type ParsedDashboardUrlFilters = {
  startDate: Date
  endDate: Date
  periodFromUrl: DashboardGraphView | null
}

export const parseDashboardFiltersFromSearchQuery = (
  searchQuery: string,
  today: Date = new Date(),
): ParsedDashboardUrlFilters | null => {
  const params = new URLSearchParams(searchQuery)
  const from = parseDashboardUrlDate(params.get('from'))
  const to = parseDashboardUrlDate(params.get('to'))
  const periodFromUrl = urlPeriodToGraphView(params.get('period'))
  if (!from || !to) return null

  const normalizedRange = normalizeRangeChronology({ startDate: from, endDate: to })
  const boundedRange = normalizeRangeChronology({
    startDate:
      normalizedRange.startDate!.getTime() > today.getTime() ? today : normalizedRange.startDate!,
    endDate:
      normalizedRange.endDate!.getTime() > today.getTime() ? today : normalizedRange.endDate!,
  })
  const finalRange = clampRangeToDayBounds({
    range: boundedRange,
    maxDate: today,
    minDays: DASHBOARD_MIN_RANGE_DAYS,
    maxDays: DASHBOARD_MAX_RANGE_DAYS,
    // Anchor on the newer date, matching Monthly → Daily period clamping.
    anchorDate: boundedRange.endDate,
  })
  if (!finalRange.startDate || !finalRange.endDate) return null

  return {
    startDate: finalRange.startDate,
    endDate: finalRange.endDate,
    periodFromUrl,
  }
}

export const buildDashboardFilterSearchQuery = (
  startDate: Date,
  endDate: Date,
  viewValue: string,
  existingSearchQuery = '',
) => {
  const nextParams = new URLSearchParams(existingSearchQuery)
  nextParams.set('from', formatMDYDotsUTC(startDate))
  nextParams.set('to', formatMDYDotsUTC(endDate))
  nextParams.set('period', graphViewToUrlPeriod(viewValue))
  return nextParams.toString()
}

export const dashboardFiltersMatchSearchQuery = (
  startDate: Date,
  endDate: Date,
  viewValue: string,
  searchQuery: string,
) => {
  const params = new URLSearchParams(searchQuery)
  return (
    params.get('from') === formatMDYDotsUTC(startDate) &&
    params.get('to') === formatMDYDotsUTC(endDate) &&
    params.get('period') === graphViewToUrlPeriod(viewValue)
  )
}
